import React, { useCallback, useState, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  AutoAwesome as AiIcon,
  MoreVert as MoreIcon,
  Save as SaveIcon,
  Close as CancelIcon,
} from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import type { HealthIssue } from '../../services/api';
import type { MappingTableRowData } from './types';
import { isTrueLiteralMapping } from '../utils/mappingTypeUtils';
import type { TypeCompatibilityIssue, SuggestedFix } from '../validation/typeCompatibility';
import type { JoinPathWarning } from '../validation/InlineValidationWarnings';
import InlineValidationWarnings from '../validation/InlineValidationWarnings';

const isLiteralRow = (row: MappingTableRowData): boolean =>
  isTrueLiteralMapping(row.transformationType, row.transformation);

const VALUE_ONLY_OPERATIONS = new Set([
  'set_constant',
  'set_null',
  'constant',
  'literal',
  'generate_uuid',
]);

const isSentinelSourceTable = (table: string): boolean =>
  table.startsWith('__') && table.endsWith('__');

export interface InlineEditValues {
  sourceTable: string;
  sourceField: string;
  transformationType: string;
  transformation: string;
  operation?: string;
  constantValue?: string;
  /** Hierarchy path in the output structure (only for hierarchical target schemas). */
  hierarchyPath?: Record<string, string>;
}

export interface MappingTableRowProps {
  row: MappingTableRowData;
  selected: boolean;
  /** Whether this row is the active/inspected row (clicked to open inspector) */
  active?: boolean;
  onToggleSelect: (id: string) => void;
  onRowClick?: (row: MappingTableRowData) => void;
  onAiSuggest?: (row: MappingTableRowData) => void;
  onOpenContextMenu?: (event: React.MouseEvent, row: MappingTableRowData) => void;
  /** Whether this row's inline editor is open */
  inlineEditOpen?: boolean;
  /** Toggle inline edit for this row */
  onToggleInlineEdit?: (rowId: string) => void;
  /** Save inline edit changes */
  onInlineEditSave?: (row: MappingTableRowData, values: InlineEditValues) => void;
  /** Available source files for inline edit dropdowns */
  sourceFiles?: string[];
  /** Fields grouped by source file */
  sourceFieldsByFile?: Record<string, string[]>;
  /** Column span for inline edit row */
  colSpan?: number;
  /** Health issues for this specific field */
  healthIssues?: HealthIssue[];
  /** Real-time type compatibility issues (from useFieldValidation) */
  typeIssues?: TypeCompatibilityIssue[];
  /** Join path warning */
  joinWarning?: JoinPathWarning | null;
  /** Callback to compute type issues when source changes during inline edit */
  onSourceChanged?: (sourceFile: string, sourceField: string) => TypeCompatibilityIssue[];
  /** Callback to apply a suggested fix to the expression */
  onApplyFix?: (fix: SuggestedFix) => void;
  /** Whether to show a hierarchy path cell in this row. */
  showHierarchyPath?: boolean;
}

/** Confidence color from 0-1 */
const confidenceColor = (c: number) =>
  c >= 0.9 ? '#10b981' : c >= 0.7 ? '#f59e0b' : '#ef4444';

/** Approval chip config (extended with schema evolution statuses) */
const approvalChipConfig: Record<string, { label: string; color: string; bg: string }> = {
  approved:  { label: '\u2713 Approved',  color: '#10b981', bg: '#10b981' },
  pending:   { label: '\u25CB Pending',   color: '#64748b', bg: '#94a3b8' },
  error:     { label: '\u2717 Error',     color: '#ef4444', bg: '#ef4444' },
  missing:   { label: '\u25CB Missing',   color: '#f59e0b', bg: '#f59e0b' },
  inherited: { label: '\u{1F512} Inherited', color: '#3b82f6', bg: '#3b82f6' },
  broken:    { label: '\u26A0 Broken',    color: '#ef4444', bg: '#ef4444' },
  stale:     { label: '\u{1F504} Stale',    color: '#f59e0b', bg: '#f59e0b' },
  // Issue #656: operator-authored mapping (manual override of AI suggestion).
  // Distinct from `approved` (which is a workflow state) — `user_authored`
  // says "the value here came from a human", regardless of approval status.
  user_authored: { label: '✎ Manual', color: '#7c3aed', bg: '#7c3aed' },
};

const TRANSFORMATION_TYPES = [
  'direct',
  'expression',
  'cast',
  'aggregate',
  'window',
  'pivot',
  'unpivot',
  'conditional',
  'filter',
  'set_constant',
  'concat',
] as const;

const operationChipColor = (op: string): string => {
  const colors: Record<string, string> = {
    aggregate: '#7c3aed',
    window: '#db2777',
    pivot: '#0891b2',
    unpivot: '#0891b2',
    conditional: '#d97706',
    filter: '#059669',
    set_constant: '#6366f1',
    concat: '#2563eb',
    cast: '#0d9488',
  };
  return colors[op] || '#64748b';
};

const MappingTableRowComponent: React.FC<MappingTableRowProps> = ({
  row,
  selected,
  active,
  onToggleSelect,
  onRowClick,
  onAiSuggest,
  onOpenContextMenu,
  inlineEditOpen,
  onToggleInlineEdit,
  onInlineEditSave,
  sourceFiles,
  sourceFieldsByFile,
  colSpan = 8,
  healthIssues,
  typeIssues: externalTypeIssues,
  joinWarning,
  onSourceChanged,
  onApplyFix: externalApplyFix,
  showHierarchyPath,
}) => {
  const statusColor = useMemo(() =>
    row.approvalStatus === 'error' ? '#ef4444'
      : row.isMapped ? '#10b981'
      : '#f59e0b',
  [row.approvalStatus, row.isMapped]);

  const isError = row.approvalStatus === 'error';

  const showLiteralSource =
    row.isMapped && (row.operation === 'set_constant' || isLiteralRow(row));

  // Inline edit local state
  const [editSource, setEditSource] = useState('');
  const [editType, setEditType] = useState(row.transformationType || 'direct');
  const [editOp, setEditOp] = useState(row.operation || row.transformationType || 'direct');
  const [editExpr, setEditExpr] = useState(row.transformation || '');
  const [editConstant, setEditConstant] = useState('');

  const isValueOnlyOp = VALUE_ONLY_OPERATIONS.has(editOp);
  const isConstantOp = editOp === 'set_constant';
  const isMissingConstant = isConstantOp && editConstant.trim().length === 0;

  // Reset local state when inline editor is opened
  React.useEffect(() => {
    if (inlineEditOpen) {
      const resolvedOp = row.operation || row.transformationType || 'direct';
      const valueOnly = VALUE_ONLY_OPERATIONS.has(resolvedOp) || isLiteralRow(row);
      setEditType(row.transformationType || 'direct');
      setEditOp(resolvedOp);
      if (valueOnly) {
        const constant =
          String(row.parameters?.value ?? row.mapping?.parameters?.value ?? row.transformation ?? '');
        setEditConstant(constant);
        setEditSource('');
        setEditExpr(resolvedOp === 'set_constant' ? constant : (row.transformation || ''));
      } else {
        const sourceTable = isSentinelSourceTable(row.sourceTable) ? '' : row.sourceTable;
        const sourceField = isSentinelSourceTable(row.sourceTable) ? '' : row.sourceField;
        setEditSource(sourceTable && sourceField ? `${sourceTable}.${sourceField}` : '');
        setEditExpr(row.transformation || '');
        setEditConstant('');
      }
    }
  }, [inlineEditOpen, row]);

  // Build source options from available source files/fields
  const sourceOptionsFlat = useMemo(() => {
    if (!sourceFiles || !sourceFieldsByFile) return [];
    const options: string[] = [];
    sourceFiles.forEach((file) => {
      const fields = sourceFieldsByFile[file] ?? [];
      fields.forEach((field) => options.push(`${file}.${field}`));
    });
    return options;
  }, [sourceFiles, sourceFieldsByFile]);

  const handleInlineSave = useCallback(() => {
    if (!onInlineEditSave || isMissingConstant) return;
    const parts = editSource.split('.');
    const sourceTable = parts[0] || '';
    const sourceField = parts.slice(1).join('.') || '';
    onInlineEditSave(row, {
      sourceTable: isValueOnlyOp ? '' : sourceTable,
      sourceField: isValueOnlyOp ? '' : sourceField,
      transformationType: isConstantOp ? 'literal' : editType,
      transformation: isConstantOp ? editConstant : editExpr,
      operation: editOp,
      constantValue: isConstantOp ? editConstant : undefined,
      hierarchyPath: row.mapping?.hierarchy_path ?? undefined,
    });
  }, [onInlineEditSave, row, editSource, editType, editExpr, editOp, editConstant, isValueOnlyOp, isConstantOp, isMissingConstant]);

  // Compute inline type issues when source changes during editing
  const inlineTypeIssues = useMemo(() => {
    if (!inlineEditOpen || isValueOnlyOp || !onSourceChanged) return externalTypeIssues || [];
    const parts = editSource.split('.');
    const sourceFile = parts[0] || '';
    const sourceField = parts.slice(1).join('.') || '';
    if (!sourceFile || !sourceField) return [];
    return onSourceChanged(sourceFile, sourceField);
  }, [inlineEditOpen, editSource, onSourceChanged, externalTypeIssues]);

  // Apply fix: auto-fill the expression field
  const handleApplyFix = useCallback((fix: SuggestedFix) => {
    setEditExpr(fix.expression);
    if (fix.fixType === 'cast' || fix.fixType === 'coalesce' || fix.fixType === 'substring' || fix.fixType === 'round') {
      setEditType('expression');
    }
    externalApplyFix?.(fix);
  }, [externalApplyFix]);

  return (
    <>
      <TableRow
        hover
        selected={selected || active}
        onClick={() => onRowClick?.(row)}
        // Issue #656: stable row id for scroll-into-view from the
        // unified Review pane / canvas selection.
        data-row-id={`${row.targetTable}.${row.targetField}`.toLowerCase()}
        sx={{
          cursor: 'pointer',
          '&.Mui-selected': {
            backgroundColor: alpha('#2563eb', 0.08),
            '& > td:first-of-type': {
              borderLeft: '3px solid #2563eb',
              pl: 0.75,
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha('#2563eb', 0.12),
          },
          '&:hover': { backgroundColor: alpha('#f1f5f9', 0.8) },
          opacity: row.isMapped ? 1 : 0.7,
          ...(isError && {
            backgroundColor: alpha('#ef4444', 0.04),
            '&:hover': { backgroundColor: alpha('#ef4444', 0.08) },
          }),
        }}
      >
        {/* Checkbox */}
        <TableCell padding="checkbox" sx={{ width: 36 }}>
          <Checkbox
            size="small"
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect(row.id)}
            sx={{ p: 0.5 }}
          />
        </TableCell>

        {/* Status */}
        <TableCell sx={{ width: 28, px: 0.5 }}>
          <Tooltip title={isError ? 'Error' : row.isMapped ? 'Mapped' : 'Unmapped'}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: statusColor,
                mx: 'auto',
              }}
            />
          </Tooltip>
        </TableCell>

        {/* Source — field name prominent, table name as subtle prefix */}
        <TableCell sx={{ maxWidth: 200 }}>
          {row.isMapped && !showLiteralSource ? (
            <Box
              title={`${row.sourceTable}.${(row.sourceFields ?? [row.sourceField]).join(', ')}`}
              sx={{ minWidth: 0 }}
            >
              <Typography variant="caption" noWrap sx={{ color: 'text.disabled', fontSize: 10, lineHeight: 1.2, display: 'block' }}>
                {row.sourceTable}
              </Typography>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {(row.sourceFields ?? [row.sourceField]).filter(Boolean).join(', ')}
              </Typography>
            </Box>
          ) : row.isMapped ? (
            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              — literal value —
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              — no source —
            </Typography>
          )}
        </TableCell>

        {/* Transformation — hide "direct" badge, only show non-trivial transformations */}
        <TableCell sx={{ maxWidth: 220, overflow: 'hidden' }}>
          {(row.operation === 'set_constant' || (row.transformation && row.transformation.toLowerCase() !== 'direct')) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
              {row.operation && !['direct', 'expression'].includes(row.operation) && (
                <Chip
                  size="small"
                  label={row.operation.toUpperCase().replace('_', ' ')}
                  sx={{
                    height: 18,
                    fontSize: 9,
                    fontWeight: 800,
                    flexShrink: 0,
                    backgroundColor: operationChipColor(row.operation),
                    color: '#fff',
                    letterSpacing: '0.03em',
                  }}
                />
              )}
              <Tooltip title={row.operation === 'set_constant'
                ? String(row.parameters?.value ?? row.mapping?.parameters?.value ?? row.transformation ?? '')
                : row.transformation}>
                <Chip
                  size="small"
                  label={row.operation === 'set_constant'
                    ? String(row.parameters?.value ?? row.mapping?.parameters?.value ?? row.transformation ?? '')
                    : row.transformation}
                  icon={row.aiGenerated ? <AiIcon sx={{ fontSize: 14 }} /> : undefined}
                  sx={{
                    maxWidth: '100%',
                    minWidth: 0,
                    height: 24,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    fontWeight: 500,
                    cursor: 'default',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          ) : row.isMapped ? (
            /* Direct mapping — show subtle dash; direct is the default */
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              —
            </Typography>
          ) : (
            /* Unmapped row action buttons */
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => { e.stopPropagation(); onRowClick?.(row); }}
                sx={{
                  fontSize: 11,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                  py: 0.25,
                  minWidth: 'auto',
                }}
              >
                Map Field
              </Button>
              <Button
                size="small"
                startIcon={<AiIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => { e.stopPropagation(); onAiSuggest?.(row); }}
                sx={{
                  fontSize: 11,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                  py: 0.25,
                  minWidth: 'auto',
                  color: '#7c3aed',
                }}
              >
                AI Suggest
              </Button>
            </Box>
          )}
        </TableCell>

        {/* Target */}
        <TableCell sx={{ maxWidth: 180, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.targetField} sx={{ fontWeight: 600, minWidth: 0 }}>
              {row.targetField}
            </Typography>
            {healthIssues && healthIssues.length > 0 && (() => {
              const exprFailures = healthIssues.filter(i => i.category === 'expression_failure');
              const otherIssues = healthIssues.filter(i => i.category !== 'expression_failure');
              const hasError = otherIssues.some(i => i.severity === 'error');
              const color = hasError ? '#ef4444' : '#f59e0b';
              const Icon = hasError ? ErrorOutlineIcon : WarningAmberIcon;
              return (
                <>
                  {otherIssues.length > 0 && (
                    <Tooltip
                      title={
                        <Box>
                          {otherIssues.slice(0, 3).map((issue, i) => (
                            <Typography key={i} variant="caption" display="block" sx={{ fontSize: 11 }}>
                              {issue.severity === 'error' ? '⛔' : '⚠️'} {issue.message}
                            </Typography>
                          ))}
                          {otherIssues.length > 3 && (
                            <Typography variant="caption" display="block" sx={{ fontSize: 11, fontStyle: 'italic' }}>
                              +{otherIssues.length - 3} more
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <Chip
                        size="small"
                        icon={<Icon sx={{ fontSize: 12 }} />}
                        label={otherIssues.length}
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          backgroundColor: alpha(color, 0.1),
                          color: color,
                          border: `1px solid ${alpha(color, 0.3)}`,
                          '& .MuiChip-icon': { color: color },
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    </Tooltip>
                  )}
                  {exprFailures.length > 0 && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" display="block" sx={{ fontSize: 11, fontWeight: 600 }}>
                            Expression failed during preview
                          </Typography>
                          {exprFailures.slice(0, 2).map((f, i) => (
                            <Typography key={i} variant="caption" display="block" sx={{ fontSize: 11 }}>
                              {f.message}
                            </Typography>
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <Chip
                        size="small"
                        icon={<FlashOnIcon sx={{ fontSize: 12 }} />}
                        label="expr"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          backgroundColor: alpha('#ef4444', 0.1),
                          color: '#ef4444',
                          border: `1px solid ${alpha('#ef4444', 0.3)}`,
                          '& .MuiChip-icon': { color: '#ef4444' },
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              );
            })()}
          </Box>
        </TableCell>

        {/* Hierarchy Path — only when hierarchical target schema */}
        {showHierarchyPath && (
          <TableCell sx={{ maxWidth: 180, overflow: 'hidden' }}>
            {row.mapping?.hierarchy_path ? (
              <Typography variant="caption" noWrap sx={{ fontSize: 11, color: 'text.secondary' }}>
                {Object.entries(row.mapping.hierarchy_path).map(([level, value]) => (
                  value === 'grouping_key' ? `${level} (key)` : level
                )).join(' > ')}
              </Typography>
            ) : null}
          </TableCell>
        )}

        {/* Confidence — colored percentage only */}
        <TableCell sx={{ width: 70, textAlign: 'center' }}>
          {row.isMapped ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: 12,
                color: confidenceColor(row.confidence),
              }}
            >
              {Math.round(row.confidence * 100)}%
            </Typography>
          ) : null}
        </TableCell>

        {/* Approval Status */}
        <TableCell sx={{ width: 90 }}>
          {(() => {
            const cfg = approvalChipConfig[row.approvalStatus] ?? approvalChipConfig.pending;
            return (
              <Chip
                size="small"
                label={cfg.label}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: alpha(cfg.bg, 0.1),
                  color: cfg.color,
                  border: `1px solid ${alpha(cfg.bg, 0.25)}`,
                }}
              />
            );
          })()}
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ width: 48, px: 0 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOpenContextMenu?.(e, row);
            }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Inline edit row */}
      {row.isMapped && inlineEditOpen && (
        <TableRow
          sx={{
            backgroundColor: alpha('#f59e0b', 0.06),
            '&:hover': { backgroundColor: alpha('#f59e0b', 0.06) },
          }}
        >
          <TableCell colSpan={colSpan} sx={{ py: 1.5, px: 2, borderBottom: '2px solid', borderColor: alpha('#f59e0b', 0.2) }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: isConstantOp ? '140px 1fr auto' : '1fr 140px 1fr auto',
              gap: 1,
              alignItems: 'end',
            }}>
              {!isValueOnlyOp && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25, display: 'block' }}>
                    Source
                  </Typography>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={sourceOptionsFlat}
                    value={editSource}
                    onChange={(_, value) => setEditSource(value ?? '')}
                    onInputChange={(_, value) => setEditSource(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ '& input': { fontSize: 12, py: 0.5 } }}
                      />
                    )}
                  />
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25, display: 'block' }}>
                  Operation
                </Typography>
                <Select
                  size="small"
                  value={editOp}
                  onChange={(e) => {
                    const nextOp = e.target.value;
                    setEditOp(nextOp);
                    setEditType(nextOp);
                    if (VALUE_ONLY_OPERATIONS.has(nextOp)) {
                      setEditSource('');
                      if (nextOp === 'set_constant') {
                        setEditExpr(editConstant);
                      }
                    }
                  }}
                  fullWidth
                  sx={{ fontSize: 12 }}
                >
                  {TRANSFORMATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>{t}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25, display: 'block' }}>
                  {isConstantOp ? 'Constant Value' : 'Expression'}
                </Typography>
                <TextField
                  size="small"
                  value={isConstantOp ? editConstant : editExpr}
                  onChange={(e) => {
                    if (isConstantOp) {
                      setEditConstant(e.target.value);
                      setEditExpr(e.target.value);
                    } else {
                      setEditExpr(e.target.value);
                    }
                  }}
                  fullWidth
                  required={isConstantOp}
                  error={isMissingConstant}
                  helperText={isMissingConstant ? 'Constant value is required' : undefined}
                  sx={{ '& input': { fontFamily: isConstantOp ? 'inherit' : 'monospace', fontSize: 12, py: 0.5 } }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, pb: 0.25 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disabled={isMissingConstant}
                  onClick={(e) => { e.stopPropagation(); handleInlineSave(); }}
                  startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 11, textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 1.5 }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); onToggleInlineEdit?.(row.id); }}
                  startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 11, textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 1.5 }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            {/* Real-time type + join warnings */}
            <InlineValidationWarnings
              typeIssues={inlineTypeIssues}
              joinWarning={joinWarning}
              onApplyFix={handleApplyFix}
              compact
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default React.memo(MappingTableRowComponent, (prev, next) => {
  // Only re-render when meaningful props change
  return (
    prev.row === next.row &&
    prev.selected === next.selected &&
    prev.active === next.active &&
    prev.inlineEditOpen === next.inlineEditOpen &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onRowClick === next.onRowClick &&
    prev.onOpenContextMenu === next.onOpenContextMenu &&
    prev.onToggleInlineEdit === next.onToggleInlineEdit &&
    prev.onInlineEditSave === next.onInlineEditSave &&
    prev.sourceFiles === next.sourceFiles &&
    prev.sourceFieldsByFile === next.sourceFieldsByFile &&
    prev.healthIssues === next.healthIssues &&
    prev.typeIssues === next.typeIssues &&
    prev.joinWarning === next.joinWarning &&
    prev.onSourceChanged === next.onSourceChanged &&
    prev.onApplyFix === next.onApplyFix
  );
});
